import React, { useState, useEffect, useRef } from 'react';
import '../../styles/navbar/AiAssistant.css';
import { useMamdani } from '../../context/MamdaniContext';

const MAMDANI_QUESTION = "How does the system detect a Mamdani bot?";

const MAMDANI_ANSWER = `Here's how our system identifies anti-Israel bots amplifying Mamdani's network:

**Step 1 — Fetch Tweets**
We pull the latest tweets from Mamdani and known anti-Israel accounts in his orbit — politicians, activists, and influencers who consistently push pro-Mamdani, anti-Israel narratives.

**Step 2 — Extract Retweeters**
For each tweet, we collect everyone who retweeted it. Retweeting is the key amplification tool — these accounts are actively pushing Mamdani's content and have a direct interest in spreading it as widely as possible.

**Step 3 — Bot Analysis**
Each retweeter is scored across multiple signals:
• Account age vs. follower ratio
• Posting frequency and timing patterns (inhuman activity bursts)
• Profile completeness — no photo, generic name, empty bio
• Engagement patterns that don't match normal human behavior

**Step 4 — Content Verification**
We visit each suspicious account's timeline to confirm: are they posting anti-Israel content? Accounts that both retweet Mamdani AND spread anti-Israel narratives are flagged as confirmed threats.

**Result**
Score 85+ → BOT | Score 70+ → LIKELY BOT | Score 50+ → SUSPICIOUS
Bots detected across multiple watchlist accounts are marked as COORDINATED — the most dangerous classification.`;

// רינדור markdown פשוט — bold +줄바꿈
function renderMarkdown(text) {
  return text.split('\n').map((line, i) => {
    const parts = line.split(/\*\*(.*?)\*\*/g);
    return (
      <React.Fragment key={i}>
        {parts.map((part, j) =>
          j % 2 === 1 ? <strong key={j}>{part}</strong> : part
        )}
        <br />
      </React.Fragment>
    );
  });
}

const AiAssistant = ({ onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showQuickQuestions, setShowQuickQuestions] = useState(true);
  const messagesEndRef = useRef(null);
  const { mamdaniMode: mm } = useMamdani();

  // שאלות מהירות - בסגנון sidebar
  const quickQuestions = mm
    ? [
        MAMDANI_QUESTION,
        "How is the bot score calculated?",
        "What are the most suspicious signs of a bot account?",
        "What does COORDINATED mean?",
      ]
    : [
        "איך המערכת מחשבת ציון בוט?",
        "איך אני יכול לזהות בוטים ברשתות חברתיות?",
        "מה הם הסימנים הכי חשודים לחשבון בוט?",
        "איך אני מתמודד עם התקפת בוטים?",
      ];

  // Auto scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // הודעת ברכה ראשונית
  useEffect(() => {
    const greetingText = mm
      ? "Hello! I'm your AI assistant from LINKS AI.\n\nI'm here to help you understand the Mamdani bot detection system, analyze suspicious accounts, and provide insights on the anti-Israel bot network."
      : 'שלום! אני העוזר האישי שלך מחברת LINKS AI\n\nאני יכול לעזור לך לנתח בוטים, לחפש חשבונות חשודים, ולספק תובנות על הרשתות.';
    setMessages([
      {
        id: '1',
        role: 'assistant',
        text: greetingText,
        timestamp: new Date(),
        isComplete: true
      }
    ]);
  }, [mm]);

  // שליחת הודעה עם Streaming אמיתי
  const handleSendMessage = async (messageText = null) => {
    const textToSend = messageText || inputText.trim();
    
    if (!textToSend || isLoading) return;

    // הסתר שאלות מהירות אחרי השאלה הראשונה
    if (showQuickQuestions) {
      setShowQuickQuestions(false);
    }

    // הוסף הודעת משתמש
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      text: textToSend,
      timestamp: new Date(),
      isComplete: true
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      console.log('🌊 Starting streaming for:', textToSend);

      const response = await fetch('https://api.anti-bot.ai/api/ai-chat-stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: textToSend
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      let streamingContent = '';
      const aiMessageId = (Date.now() + 1).toString();
      
      const emptyAiMessage = {
        id: aiMessageId,
        role: 'assistant',
        text: '',
        timestamp: new Date(),
        isComplete: false,
        isStreaming: true
      };
      
      setMessages(prev => [...prev, emptyAiMessage]);
      setIsLoading(false);

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          console.log('✅ Stream completed');
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'content') {
                streamingContent += data.content;
                
                setMessages(prev => 
                  prev.map(msg => 
                    msg.id === aiMessageId 
                      ? { ...msg, text: streamingContent }
                      : msg
                  )
                );
                
              } else if (data.type === 'end') {
                console.log(`✅ Stream finished: ${data.tokens} tokens`);
                
                setMessages(prev => 
                  prev.map(msg => 
                    msg.id === aiMessageId 
                      ? { 
                          ...msg, 
                          text: data.full_response,
                          isComplete: true,
                          isStreaming: false
                        }
                      : msg
                  )
                );
                
              } else if (data.type === 'error') {
                console.error('❌ Stream error:', data.message);
                
                setMessages(prev => 
                  prev.map(msg => 
                    msg.id === aiMessageId 
                      ? { 
                          ...msg, 
                          text: data.message,
                          isComplete: true,
                          isStreaming: false
                        }
                      : msg
                  )
                );
              }
              
            } catch (parseError) {
              console.warn('Parse error:', parseError);
            }
          }
        }
      }

    } catch (error) {
      console.error('❌ Streaming Error:', error);
      
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        text: 'לא הצלחתי להתחבר לשרת. בדוק שהשרת פועל ונסה שוב.',
        timestamp: new Date(),
        isComplete: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // סימולציית streaming לתשובה מוכנה
  const streamText = (fullText, messageId) => {
    let i = 0;
    const iv = setInterval(() => {
      i += 2;
      const chunk = fullText.slice(0, i);
      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? { ...msg, text: chunk, isStreaming: i < fullText.length }
          : msg
      ));
      if (i >= fullText.length) {
        clearInterval(iv);
        setMessages(prev => prev.map(msg =>
          msg.id === messageId
            ? { ...msg, text: fullText, isStreaming: false, isComplete: true }
            : msg
        ));
      }
    }, 30);
  };

  // לחיצה על שאלה מהירה
  const handleQuickQuestion = (questionText) => {
    if (questionText === MAMDANI_QUESTION) {
      setShowQuickQuestions(false);
      const aiId = (Date.now() + 1).toString();
      const userMsg = {
        id: Date.now().toString(),
        role: 'user',
        text: questionText,
        timestamp: new Date(),
        isComplete: true,
      };
      const aiMsg = {
        id: aiId,
        role: 'assistant',
        text: '',
        timestamp: new Date(),
        isComplete: false,
        isStreaming: true,
      };
      setMessages(prev => [...prev, userMsg, aiMsg]);
      setTimeout(() => streamText(MAMDANI_ANSWER, aiId), 300);
      return;
    }
    setInputText(questionText);
    handleSendMessage(questionText);
  };

  // טיפול ב-Enter
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className={`ai-assistant-container ${onClose ? 'as-popup' : 'as-page'}`}>
      <div className="ai-assistant-window">
        
        {/* Header */}
        <div className="ai-header">
          <div className="ai-title">עוזר אישי</div>
          <button className="ai-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Messages */}
        <div className="ai-messages">
          {messages.map((message) => (
            <div key={message.id} className={`ai-message ${message.role}`}>
              <div className="ai-message-text">
                {renderMarkdown(message.text)}
                {message.isStreaming && (
                  <span className="streaming-cursor">|</span>
                )}
              </div>
              <div className="ai-message-time">
                {message.timestamp.toLocaleTimeString('he-IL', { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
                {message.isStreaming && (
                  <span className="streaming-indicator"> • כותב...</span>
                )}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="ai-message assistant">
              <div className="ai-message-text">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Questions - בסגנון sidebar */}
        {showQuickQuestions && (
          <div className="sidebar-quick-questions">
            {quickQuestions.map((question, index) => (
              <button
                key={index}
                className="sidebar-question-btn"
                onClick={() => handleQuickQuestion(question)}
              >
                {question}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="ai-input-container">
          <input
            type="text"
            className="ai-input"
            placeholder="כתוב הודעה..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
          />
          <button 
            className="ai-send-btn"
            onClick={() => handleSendMessage()}
            disabled={isLoading || !inputText.trim()}
          >
            ➤
          </button>
        </div>

      </div>
    </div>
  );
};

export default AiAssistant;